// Red banner shown at the top of the public app whenever an admin is
// impersonating a user. Presence is signalled by the `adminReturnToken`
// key in localStorage (written by /impersonate landing route).
//
// The "Return to admin" button clears public-app auth state first, then
// hard-navigates the browser to the admin app's /impersonate/return route
// with the return JWT in the query string. That route POSTs to the API's
// restore endpoint, drops the admin's cookie back in place, and lands
// them on the users list. The admin's `admin_token` cookie itself was
// never touched during impersonation (cookies are per-origin), so the
// return flow is really about handing the JWT back for a fresh mint.

import { useEffect, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useSelector } from "react-redux";

import { removeToken } from "../../utils";
import { IMPERSONATION_KEYS } from "../../Pages/Impersonate/ImpersonateLanding";

export default function ImpersonationBanner() {
  const firstName = useSelector((s) => s.main.firstName);
  const lastName = useSelector((s) => s.main.lastName);
  const bannerRef = useRef(null);
  // Re-read on every mount + refresh trigger. localStorage doesn't fire a
  // React re-render on its own, so we use a mount-time snapshot plus a
  // 1s poll — cheap, and the banner only mounts when it's already there.
  const [returnToken, setReturnToken] = useState(() =>
    typeof window !== "undefined"
      ? localStorage.getItem(IMPERSONATION_KEYS.returnToken)
      : null
  );

  useEffect(() => {
    const id = setInterval(() => {
      const current = localStorage.getItem(IMPERSONATION_KEYS.returnToken);
      setReturnToken((prev) => (prev === current ? prev : current));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // When the banner is visible, pin it to the top of the viewport (above the
  // fixed navbar) and shift everything below down by the banner's height so
  // nothing overlaps and the banner never scrolls away. Two pieces are needed
  // because the navbar is position:fixed (viewport-anchored), so body padding
  // alone won't move it — the injected rule bumps its `top`, and body padding
  // handles the rest of the (in-flow) page content.
  useEffect(() => {
    if (!returnToken || !bannerRef.current) return undefined;
    const h = bannerRef.current.getBoundingClientRect().height;
    const prevBodyPad = document.body.style.paddingTop;
    document.body.style.paddingTop = `${h}px`;
    const style = document.createElement("style");
    style.setAttribute("data-impersonation-banner", "");
    style.textContent = `.navbar { top: ${h}px !important; }`;
    document.head.appendChild(style);
    return () => {
      document.body.style.paddingTop = prevBodyPad;
      style.remove();
    };
  }, [returnToken]);

  if (!returnToken) return null;

  const handleReturn = () => {
    const token = localStorage.getItem(IMPERSONATION_KEYS.returnToken);
    const adminUrl =
      process.env.REACT_APP_ADMIN_APP_URL || "http://localhost:3005"; // matches CLAUDE.md convention
    // Clear public-app auth state BEFORE navigating away, so that even if
    // the admin app fails to restore the session, we don't leave a stale
    // impersonated session lingering here.
    removeToken();
    localStorage.removeItem(IMPERSONATION_KEYS.returnToken);
    localStorage.removeItem(IMPERSONATION_KEYS.impersonatedBy);
    window.location.href = `${adminUrl}/impersonate/return?token=${encodeURIComponent(
      token
    )}&returnTo=/user`;
  };

  const displayName = [firstName, lastName].filter(Boolean).join(" ").trim();

  return (
    <Box
      ref={bannerRef}
      role="status"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1300, // above .navbar (z-index 999)
        backgroundColor: "#d32f2f",
        color: "#fff",
        py: 0.75,
        px: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: 2,
        fontFamily: "inherit",
      }}
    >
      <Typography variant="body2" sx={{ color: "inherit" }}>
        You are impersonating{" "}
        <Box component="strong" sx={{ fontWeight: 700 }}>
          {displayName || "this user"}
        </Box>
        .
      </Typography>
      <Button
        size="small"
        variant="outlined"
        onClick={handleReturn}
        sx={{
          color: "#fff",
          borderColor: "#fff",
          textTransform: "none",
          fontWeight: 600,
          "&:hover": { borderColor: "#fff", backgroundColor: "rgba(255,255,255,0.1)" },
        }}
      >
        Return to admin
      </Button>
    </Box>
  );
}
